import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  SITE_NOTICES_CATALOGUE,
  parseNoticesList,
  serializeNoticesList,
} from '../constants/riskAssessment';

/**
 * Site notices selector (Chunk F reframed).
 *
 * RA-level callouts that describe the shoot (Hot Set, Pyro Active, Wind
 * Hold, etc.). Multi-select chip picker; selected notices render as a
 * prominent amber banner on the PDF cover page.
 *
 * Two modes via `readOnly` prop — same shape as PpePicker / RegsSelector.
 */
export default function SiteNoticesSelector({
  value,
  onChange,
  onCommit,
  disabled = false,
  readOnly = false,
}) {
  const initialSet = useMemo(() => new Set(parseNoticesList(value)), [value]);
  const [selected, setSelected] = useState(initialSet);

  useEffect(() => {
    setSelected(new Set(parseNoticesList(value)));
  }, [value]);

  const toggle = useCallback(
    (slug) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        const arr = [...next];
        const str = serializeNoticesList(arr);
        onChange?.(str);
        onCommit?.(str);
        return next;
      });
    },
    [onChange, onCommit]
  );

  if (readOnly) {
    const items = SITE_NOTICES_CATALOGUE.filter((n) => selected.has(n.slug));
    if (items.length === 0) {
      return (
        <div className="mt-1 text-[11px] italic text-gray-400 dark:text-gray-500">
          No site notices.
        </div>
      );
    }
    return (
      <div className="mt-1 flex flex-wrap gap-2">
        {items.map((n) => (
          <span
            key={n.slug}
            title={n.sub}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 ring-1 ring-amber-300 dark:ring-amber-700"
          >
            <span className="text-base leading-none">{n.emoji}</span>
            {n.label}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {SITE_NOTICES_CATALOGUE.map((n) => {
        const isOn = selected.has(n.slug);
        return (
          <button
            key={n.slug}
            type="button"
            disabled={disabled}
            onClick={() => toggle(n.slug)}
            title={n.sub}
            aria-pressed={isOn}
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded transition-all ${
              isOn
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 ring-1 ring-amber-400 dark:ring-amber-500'
                : 'bg-transparent text-gray-500 dark:text-gray-400 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-amber-400 dark:hover:ring-amber-500'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className={`text-base leading-none ${isOn ? '' : 'opacity-50'}`} aria-hidden="true">
              {n.emoji}
            </span>
            <span>{n.label}</span>
          </button>
        );
      })}
    </div>
  );
}
